import { DecimalPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  DestroyRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  model,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxDropdownComponent } from '../../dropdown/onyx-dropdown/onyx-dropdown.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { ONYX_PAGINATION, PAGINATION_LIMIT_OPTIONS } from '../constants';
import type { OnyxPagination } from '../interfaces';

@Component({
  selector: 'onyx-pagination',
  imports: [
    OnyxDropdownComponent,
    OnyxIconComponent,
    TranslatePipe,
    ReactiveFormsModule,
    DecimalPipe,
  ],
  templateUrl: './onyx-pagination.component.html',
  styleUrl: './onyx-pagination.component.scss',
  host: {
    '[style.padding.top.px]': `padding()`,
    '[style.padding.bottom.px]': `padding()`,
  },
})
export class OnyxPaginationComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  public readonly pagination = model.required<OnyxPagination>();
  public readonly totalItems = input.required({
    transform: (totalItems: number | null | undefined): number =>
      totalItems ?? 0,
  });
  public readonly showLimit = input(true);
  public readonly padding = input(8);

  protected readonly I18N = `${I18N_NAMESPACE}.pagination`;
  protected readonly LIMIT_OPTIONS = PAGINATION_LIMIT_OPTIONS;

  protected limitControl = this.fb.control(ONYX_PAGINATION.limit);

  protected pageItemsTranslation = computed(() => {
    const pagination = this.pagination();
    return (
      (pagination.page - 1) * pagination.limit +
      1 +
      '-' +
      (pagination.page < this.pages().length
        ? pagination.page * pagination.limit
        : this.totalItems())
    );
  });
  protected pages = computed(() =>
    Array.from({
      length: Math.ceil(this.totalItems() / this.pagination().limit),
    }).map((_, index) => index + 1),
  );

  public ngOnInit(): void {
    toObservable(this.pagination, { injector: this.injector })
      .pipe(
        map((pagination) => pagination.limit),
        distinctUntilChanged(),
        filter(
          (limit) =>
            limit !== this.limitControl.value &&
            PAGINATION_LIMIT_OPTIONS.map((option) => option.value).includes(
              limit,
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((limit) => this.limitControl.setValue(limit));

    const limit = toSignal(this.limitControl.valueChanges, {
      initialValue: this.limitControl.value,
      injector: this.injector,
    });
    effect(() => this.changeLimit(limit()), { injector: this.injector });

    effect(
      () => {
        const maxPage = Math.max(this.pages().length, 1);
        if (this.pagination().page > maxPage) this.changePage(maxPage);
      },
      { injector: this.injector },
    );
  }

  protected previousPage(): void {
    const page = this.pagination().page;
    if (page <= 1) return;

    this.changePage(page - 1);
  }

  protected nextPage(): void {
    const page = this.pagination().page;
    if (page >= this.pages().length) return;

    this.changePage(page + 1);
  }

  protected changePage(page: number): void {
    this.pagination.update((pagination) => ({
      ...pagination,
      page,
    }));
  }

  private changeLimit(limit: number): void {
    this.pagination.update((pagination) => ({
      page:
        Math.floor(((pagination.page - 1) * pagination.limit + 1) / limit) + 1,
      limit,
    }));
  }
}
